import posthog from "posthog-js";
import {
    ENV,
    PORTAL_BASE_URL,
    REGISTER_POSTHOG_API_KEY,
    POSTHOG_API_HOST,
} from "../env";
import { getUtmParams } from "./registerRedirect";

const appendUtmsToPath = (path, utms) => {
    if (!path) return "";
    const [base, existingQuery = ""] = path.split("?");
    const search = new URLSearchParams(existingQuery);
    Object.entries(utms).forEach(([key, value]) => {
        if (!search.has(key)) search.set(key, value);
    });
    const queryString = search.toString();
    return queryString ? `${base}?${queryString}` : base;
};

const buildPortalUrl = (route, extraParams, redirectURL) => {
    const utms = getUtmParams();
    const params = new URLSearchParams();
    Object.entries(extraParams).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== "") params.set(k, v);
    });
    if (redirectURL) params.set("redirectURL", appendUtmsToPath(redirectURL, utms));
    Object.entries(utms).forEach(([k, v]) => params.set(k, v));
    return `${PORTAL_BASE_URL}${route}?${params.toString()}`;
};

const HANDOFF_COOKIE = "__all3d_signup_handoff";
const HANDOFF_TTL_SECONDS = 60;

const isSecureContext = () =>
    typeof window !== "undefined" && window.location.protocol === "https:";

const cookieDomainForEnv = () => {
    if (ENV === "production" || ENV === "staging") return ".all3d.ai";
    return null;
};

const writeHandoffCookie = (password) => {
    if (typeof document === "undefined") return;
    const encoded = encodeURIComponent(window.btoa(unescape(encodeURIComponent(password))));
    const parts = [
        `${HANDOFF_COOKIE}=${encoded}`,
        `Max-Age=${HANDOFF_TTL_SECONDS}`,
        "Path=/",
        "SameSite=Lax",
    ];
    const domain = cookieDomainForEnv();
    if (domain) parts.push(`Domain=${domain}`);
    if (isSecureContext()) parts.push("Secure");
    document.cookie = parts.join("; ");
};

const trackSignupSubmit = ({ email, utm_source }) => {
    try {
        const ph = posthog.init(REGISTER_POSTHOG_API_KEY, {
            api_host: POSTHOG_API_HOST,
            autocapture: false,
            name: "register",
            capture_pageview: false,
            disable_session_recording: true,
            loaded: (instance) => {
                if (utm_source) instance.register({ utm_source });
            },
        });
        if (ph) {
            ph.capture("signup_popup_submit", {
                location: window.location.pathname,
                utm_source: utm_source || "direct",
                email,
            });
        }
    } catch (e) {
        // PostHog must never block signup
    }
};

export const buildDefaultRedirectURL = () => {
    const utms = getUtmParams();
    return appendUtmsToPath(
        "/select-subscription?plan=free_trial_ai&plan_type=smb_3d_services",
        utms
    );
};

export const readUtmSource = () => {
    if (typeof window === "undefined") return null;
    const urlParams = new URLSearchParams(window.location.search);
    return localStorage.getItem("utm_source") || urlParams.get("utm_source");
};

export const startSignupHandoff = ({ email, password, redirectURL }) => {
    const utm_source = readUtmSource();
    trackSignupSubmit({ email, utm_source });
    writeHandoffCookie(password);

    const finalRedirectURL = redirectURL || buildDefaultRedirectURL();
    window.location.href = buildPortalUrl(
        "/register",
        { email, prefilled: 1 },
        finalRedirectURL
    );
};

export const startGoogleSignupHandoff = ({ redirectURL } = {}) => {
    const finalRedirectURL = redirectURL || buildDefaultRedirectURL();
    window.location.href = buildPortalUrl(
        "/register",
        { google: 1 },
        finalRedirectURL
    );
};

// Send the visitor to the portal's /login. Login.tsx already auto-bounces
// already-signed-in users to redirectURL on mount, and (with the matching
// patch) honors redirectURL after a fresh login submit too.
export const startLoginHandoff = ({ redirectURL } = {}) => {
    const finalRedirectURL = redirectURL || buildDefaultRedirectURL();
    window.location.href = buildPortalUrl("/login", {}, finalRedirectURL);
};
